import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Building2, Mail, Phone, ArrowLeft, Clock, User, Loader2 } from 'lucide-react';

export default function EventDetail() {
  const { uuid } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await fetch(`https://api.alumni.uem.mz/api/v1/events/${uuid}/public`);
        if (!response.ok) throw new Error('Evento não encontrado');
        const data = await response.json();
        setEvent(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [uuid]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Data a definir';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatStatus = (status) => {
    if (!status) return { label: 'Sem estado', color: 'bg-gray-100 text-gray-600' };
    const map = {
      proximo: { label: 'Próximo', color: 'bg-emerald-50 text-emerald-700' },
      a_decorrer: { label: 'A decorrer', color: 'bg-blue-50 text-blue-700' },
      encerrado: { label: 'Encerrado', color: 'bg-gray-50 text-gray-600' },
    };
    return map[status] || { label: status, color: 'bg-gray-50 text-gray-600' };
  };

  if (loading) {
    return (
      <div className="w-full py-6">
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
          <span className="text-gray-400 text-sm">Carregando evento...</span>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="w-full py-6">
        <div className="bg-gray-50/50 rounded-xl p-12 text-center border border-gray-100">
          <p className="text-gray-600 mb-4">{error || 'Evento não encontrado.'}</p>
          <Link to="/eventos" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Voltar para eventos
          </Link>
        </div>
      </div>
    );
  }

  const scheduleItems = event.schedule ? event.schedule.split('#').filter(item => item.trim()) : [];
  const statusBadge = formatStatus(event.status);
  const hasContact = event.contact_email || event.contact_phone;

  return (
    <div className="w-full py-6">
      {/* Navigation */}
      <div className="mb-8">
        <Link
          to="/eventos"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Eventos
        </Link>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Hero Image */}
        {event.image && (
          <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
            <img 
              src={event.image} 
              alt={event.title} 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </div>
        )}

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Status Badge */}
          <div className="mb-6">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusBadge.color}`}>
              {statusBadge.label}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-light text-gray-800 tracking-tight mb-6">
            {event.title}
          </h1>

          {/* Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 pb-8 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Data</p>
                <p className="text-sm text-gray-700">{formatDate(event.date)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Local</p>
                <p className="text-sm text-gray-700">{event.location || 'Local a definir'}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Organizado por</p>
                <p className="text-sm text-gray-700">{event.organized_by || '—'}</p>
              </div>
            </div>
          </div>

          {/* About Section */}
          {event.about && (
            <div className="mb-8 pb-8 border-b border-gray-100">
              <h2 className="text-base font-medium text-gray-800 mb-3">Sobre o evento</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {event.about}
              </p>
            </div>
          )}

          {/* Schedule Section */}
          {scheduleItems.length > 0 && (
            <div className="mb-8 pb-8 border-b border-gray-100">
              <h2 className="text-base font-medium text-gray-800 mb-4">Programação</h2>
              <div className="space-y-3">
                {scheduleItems.map((item, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div className="flex-1 text-gray-600 text-sm leading-relaxed">
                      {item}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact Section */}
          {hasContact && (
            <div>
              <h2 className="text-base font-medium text-gray-800 mb-4">Informações de Contacto</h2>
              <div className="space-y-3">
                {event.contact_email && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Email</p>
                      <a 
                        href={`mailto:${event.contact_email}`} 
                        className="text-sm text-gray-700 hover:text-emerald-600 transition-colors"
                      >
                        {event.contact_email}
                      </a>
                    </div>
                  </div>
                )}
                {event.contact_phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Phone className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Contacto</p>
                      <a 
                        href={`tel:${event.contact_phone}`} 
                        className="text-sm text-gray-700 hover:text-emerald-600 transition-colors"
                      >
                        {event.contact_phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}